import type { VariableExpenseGridQuery } from '@clockwise/clients/customer-variable-expense-occurrences';
import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { variableExpenseOccurrencesApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The period the download is generated for. The import itself is NOT scoped
   *  to it — a file may carry any dates — but the sheet has to know which
   *  month's lines to lay out. */
  query: VariableExpenseGridQuery;
}

/**
 * Import amounts from the filled-in monthly sheet.
 *
 * The description says what makes this importer different from every other one
 * in the app: it does not upsert. An occurrence has no natural key — two fees
 * on one day are two real charges — so each row is an insert, and a row that
 * duplicates one already recorded is skipped rather than added twice. Users who
 * have imported anything else here will otherwise assume re-importing replaces.
 */
export function VariableExpenseImportSheet({ open, onOpenChange, query }: Props) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      description="Upload the filled-in .xlsx or .csv. Rows are matched to an expense line by Client ID, expense item and date. Blank amounts are skipped, and a row that repeats one already recorded is not added twice."
      // The "template" here IS the export — see the client. It carries a blank
      // row for every line with no amount, and rows that already have one come
      // back as `unchanged`, so downloading it and re-uploading it can only
      // ever create the amounts you actually typed.
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await variableExpenseOccurrencesApi.exportXlsx(query);
          triggerDownload(blob, filename ?? 'variable-expenses.xlsx');
        } catch (err) {
          toast.error(err instanceof ApiError ? err.message : 'Template download failed');
        }
      }}
      onImport={async (file, { idempotencyKey }) => {
        const res = await variableExpenseOccurrencesApi.importFile(file, { idempotencyKey });
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          unchanged: res.data.unchanged,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            // Structured errors pass straight through — the generic
            // ImportSheet renders `{ code, message, field }` with a field chip,
            // which is what anchors an AMBIGUOUS_LINE error on the Line column.
            errors: r.errors,
          })),
        };
      }}
      onOpenChange={onOpenChange}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.variableExpenseOccurrences.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      }}
      open={open}
      title="Import variable expense amounts"
    />
  );
}
